import type { PiiBackfillStateDto } from "@babackapp/private-api-client/generated/schema";

export const BACKFILL_STATUSES = {
  never: "Jamais lancé",
  running: "En cours",
  paused: "En pause",
  partial: "À reprendre",
  done: "Terminé",
} as const;

export type BackfillStatus = keyof typeof BACKFILL_STATUSES;

export type ShopBackfillSummary = {
  status: BackfillStatus;
  /** Oldest order covered when orders were windowed. */
  ordersSince: number | null;
  /** Share of documents walked across the three collections, 0 to 100. */
  percent: number;
};

const COLLECTION_COUNT = 3;

const statusOf = (states: PiiBackfillStateDto[]): BackfillStatus => {
  if (states.length === 0) return "never";
  if (states.some((state) => !state.isDone && !state.isPaused)) {
    return "running";
  }
  if (states.some((state) => state.isPaused)) return "paused";
  const isComplete =
    states.length === COLLECTION_COUNT && states.every((state) => state.isDone);
  return isComplete ? "done" : "partial";
};

/** A shop's backfill in one line, from its per-collection progress rows. */
export const summarize = (
  states: PiiBackfillStateDto[],
): ShopBackfillSummary => {
  const total = states.reduce((sum, state) => sum + state.total, 0);
  const scanned = states.reduce(
    (sum, state) => sum + Math.min(state.scanned, state.total),
    0,
  );
  const status = statusOf(states);
  return {
    status,
    ordersSince:
      states.find((state) => state.collection === "order")?.since ?? null,
    // A finished shop without documents walked all of them: none.
    percent:
      total > 0
        ? Math.round((scanned / total) * 100)
        : status === "done"
          ? 100
          : 0,
  };
};
